import { NextPage } from 'next';

import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Divider, Typography } from '@mui/material';

import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

import styles from './About.module.css';

const ORIGIN = 'https://www.findmyprops.com';

const ABOUT_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${ORIGIN}/about#aboutpage`,
    url: `${ORIGIN}/about`,
    name: 'About FindMyProps — Transparent Real Estate for India',
    description:
      'FindMyProps is an online real estate marketplace built to make Indian property transactions transparent, efficient, and trustworthy. We connect verified buyers directly with verified sellers and agents.',
    publisher: {
      '@type': 'Organization',
      '@id': `${ORIGIN}/#organization`,
      name: 'FindMyProps',
      url: ORIGIN,
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/icon.svg` },
      sameAs: [
        'https://www.instagram.com/findmyprops/',
        'https://www.linkedin.com/in/pallavi-r-943b2a35b/',
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': `${ORIGIN}/#organization`,
    name: 'FindMyProps',
    url: ORIGIN,
    description:
      'FindMyProps is an online real estate marketplace connecting verified buyers with verified sellers and professional agents across India.',
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
    serviceType: 'Online Real Estate Marketplace',
    availableLanguage: ['English', 'Tamil'],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@findmyprops.com',
      contactType: 'customer support',
    },
    sameAs: [
      'https://www.instagram.com/findmyprops/',
      'https://www.linkedin.com/in/pallavi-r-943b2a35b/',
    ],
  },
];

const features = [
  {
    icon: <VerifiedIcon sx={{ fontSize: 36 }} />,
    title: 'Verified Buyers & Agents',
    body: 'Every user on FindMyProps completes phone OTP verification. Agents receive an additional verified badge after profile review. You only ever deal with real, accountable people.',
  },
  {
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 36 }} />,
    title: 'Direct Chat — No Middlemen',
    body: 'Buyers connect directly with sellers and agents through in-platform messaging. No contact-detail paywalls, no per-lead charges. Every conversation is stored and searchable.',
  },
  {
    icon: <SearchIcon sx={{ fontSize: 36 }} />,
    title: 'Powerful Property Search',
    body: 'Search residential plots, houses, farm land, and commercial land across India. Filter by locality, price, area, facing direction, approvals, and more.',
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 36 }} />,
    title: 'Built for Agents Too',
    body: 'Listing analytics, reaction data, and the Agents Knowledge Hub give professional agents the tools and market intelligence to close more deals faster.',
  },
];

const About: NextPage = () => {
  return (
    <PageLayout
      pageTitle="About FindMyProps — Transparent Real Estate for India"
      metaDescription="FindMyProps is an online real estate marketplace built to make Indian property transactions transparent. We connect verified buyers directly with verified sellers and agents across South India."
      route={ROUTES.AboutUs}
      showPropertyLinksGrid={false}
      jsonLd={ABOUT_JSON_LD}
    >
      <PageLayout.SectionContainer className={styles.root}>

        {/* Hero */}
        <Box className={styles.hero}>
          <Typography variant="h2" className={styles.heroTitle}>
            About FindMyProps
          </Typography>
          <Typography variant="body1" className={styles.heroSubtitle}>
            We built FindMyProps because real estate in India deserved better —
            transparent pricing, verified contacts, and technology that works for
            both buyers and agents.
          </Typography>
        </Box>

        <Divider className={styles.divider} />

        {/* Mission */}
        <Box className={styles.section}>
          <Typography variant="h4" className={styles.sectionTitle}>
            Our Mission
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            Real estate is one of the most significant financial decisions in any
            family's life. Yet the Indian property market has historically been
            opaque — unverified listings, anonymous agents, and hidden costs.
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            FindMyProps exists to change that. We are an online real estate
            marketplace that connects <strong>verified buyers</strong> directly
            with <strong>verified sellers and professional agents</strong> across
            India — eliminating middlemen, fake listings, and the friction that
            slows down honest transactions.
          </Typography>
        </Box>

        <Divider className={styles.divider} />

        {/* Features grid */}
        <Box className={styles.section}>
          <Typography variant="h4" className={styles.sectionTitle}>
            What Makes FindMyProps Different
          </Typography>
          <Box className={styles.featuresGrid}>
            {features.map(f => (
              <Box key={f.title} className={styles.featureCard}>
                <Box className={styles.featureIcon}>{f.icon}</Box>
                <Typography variant="h6" className={styles.featureTitle}>
                  {f.title}
                </Typography>
                <Typography variant="body2" className={styles.featureBody}>
                  {f.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider className={styles.divider} />

        {/* Coverage */}
        <Box className={styles.section}>
          <Typography variant="h4" className={styles.sectionTitle}>
            Where We Operate
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            FindMyProps is available across India with the deepest market
            coverage in South India:
          </Typography>
          <Box component="ul" className={styles.coverageList}>
            <li>
              <strong>Tamil Nadu</strong> — Chennai, Coimbatore, Madurai,
              Tiruppur, Salem, Hosur, Erode, Tiruchirappalli, Vellore, Thanjavur
            </li>
            <li>
              <strong>Andhra Pradesh</strong> — Nellore, Vijayawada, Tirupati,
              Guntur, Krishnapatnam
            </li>
            <li>
              <strong>Karnataka</strong> — Bengaluru periphery, Hosur corridor,
              Tumkur
            </li>
          </Box>
          <Typography variant="body1" className={styles.bodyText}>
            Coverage expands continuously as new verified agents and sellers join
            the platform.
          </Typography>
        </Box>

        <Divider className={styles.divider} />

        {/* Contact */}
        <Box className={styles.section}>
          <Typography variant="h4" className={styles.sectionTitle}>
            Get in Touch
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            Have a question, partnership inquiry, or feedback? We would love to
            hear from you.
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            Email:{' '}
            <Box
              component="a"
              href="mailto:support@findmyprops.com"
              className={styles.link}
            >
              support@findmyprops.com
            </Box>
          </Typography>
          <Typography variant="body1" className={styles.bodyText}>
            Follow us:{' '}
            <Box
              component="a"
              href="https://www.instagram.com/findmyprops/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              Instagram
            </Box>
            {' · '}
            <Box
              component="a"
              href="https://www.linkedin.com/in/pallavi-r-943b2a35b/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              LinkedIn
            </Box>
          </Typography>
        </Box>

      </PageLayout.SectionContainer>
    </PageLayout>
  );
};

export default About;